import type { ReactNode } from "react";
import { Icone, type NomeIcone } from "@/components/Icone";

/**
 * O que o WhatsApp mostra formatado também sai formatado aqui: *negrito*,
 * _itálico_ e ~riscado~. Sem isto a recepção lia "*08:00*" com os asteriscos.
 * Só conta o marcador colado em começo/fim de palavra — `nome_do_arquivo` e
 * "2*3" ficam como estão.
 */
const FORMATO = /(^|[\s(«"'])([*_~])(?=\S)([^\n]*?\S)\2(?=$|[\s.,!?;:)»"'])/g;
function formatar(texto: string): ReactNode[] {
  const partes: ReactNode[] = [];
  let fim = 0;
  let n = 0;
  for (const m of texto.matchAll(FORMATO)) {
    const inicio = m.index + m[1].length;
    partes.push(texto.slice(fim, inicio));
    const dentro = m[3];
    partes.push(
      m[2] === "*" ? <strong key={n++}>{dentro}</strong> : m[2] === "_" ? <em key={n++}>{dentro}</em> : <s key={n++}>{dentro}</s>,
    );
    fim = m.index + m[0].length;
  }
  partes.push(texto.slice(fim));
  return partes;
}

/**
 * O áudio e a imagem chegam ao banco já convertidos em texto, com um rótulo na
 * frente (`lib/midia/porta.ts`). Aqui o rótulo vira o ícone e o texto vira a
 * transcrição ou a descrição — a recepção vê que foi áudio sem ler colchete.
 * O arquivo em si não é guardado, por isso não há botão de tocar.
 */
const MIDIA: Array<{ prefixo: string; icone: NomeIcone; rotulo: string; texto: string }> = [
  { prefixo: "[Áudio do cliente — transcrição]", icone: "microfone", rotulo: "Áudio", texto: "Transcrição" },
  { prefixo: "[Imagem do cliente — descrição]", icone: "imagem", rotulo: "Imagem", texto: "O que tem na imagem" },
];

/**
 * Uma mensagem na conversa, como num app de mensagem: o paciente à esquerda
 * em branco, o que sai da clínica à direita, com a cauda no canto de cima.
 *
 * Quem falou vai DENTRO da bolha, no alto — "Assistente", "Equipe" ou
 * "Modelo aprovado". Sem isso, a resposta da recepção e a do assistente se
 * confundem quando alguém assume no meio da conversa.
 */
export function Balao({ autor, texto, hora }: { autor: string; texto: string; hora: string }) {
  const doCliente = autor === "cliente";
  const midia = doCliente ? MIDIA.find((m) => texto.startsWith(m.prefixo)) : undefined;

  const estilo = doCliente
    ? "rounded-tl-[4px] bg-[var(--color-cliente)] text-[var(--color-tinta)]"
    : autor === "humano"
      ? "rounded-tr-[4px] bg-[var(--color-humano)] text-[var(--color-humano-tinta)]"
      : autor === "modelo"
        ? "rounded-tr-[4px] bg-[var(--color-cartao)] text-[var(--color-tinta)] shadow-[inset_0_0_0_1.5px_var(--color-marca)]"
        : "rounded-tr-[4px] bg-[var(--color-agente)] text-[var(--color-agente-tinta)]";

  const quem = doCliente
    ? null
    : autor === "humano"
      ? { icone: "pessoa" as const, rotulo: "Equipe", cor: "text-[var(--color-atencao)]" }
      : autor === "modelo"
        ? { icone: "modelos" as const, rotulo: "Modelo aprovado", cor: "text-[var(--color-marca-forte)]" }
        : { icone: "agente" as const, rotulo: "Assistente", cor: "text-[var(--color-marca-forte)]" };

  return (
    <div className={doCliente ? "flex justify-start" : "flex justify-end"}>
      <div
        className={
          "max-w-[84%] rounded-[14px] px-3 pt-2 pb-1.5 text-[14.5px] leading-[1.45] shadow-[0_1px_1px_rgba(0,0,0,0.05)] md:max-w-[min(68%,560px)] dark:shadow-none " +
          estilo
        }
      >
        {quem && (
          <p className={"mb-0.5 flex items-center gap-1 text-xs font-bold " + quem.cor}>
            <Icone nome={quem.icone} className="h-3 w-3" />
            {quem.rotulo}
          </p>
        )}

        {midia ? (
          <>
            <p className="flex items-center gap-1.5 text-[13px] font-semibold text-[var(--color-suave)]">
              <Icone nome={midia.icone} className="h-4 w-4" />
              {midia.rotulo}
            </p>
            <p className="mt-1.5 border-t border-dashed border-[var(--color-borda-forte)] pt-1.5 text-[13.5px] whitespace-pre-wrap text-[var(--color-suave)] italic">
              <span className="sr-only">{midia.texto}: </span>
              {texto.slice(midia.prefixo.length).trim()}
            </p>
          </>
        ) : (
          <p className="whitespace-pre-wrap [overflow-wrap:anywhere]">{formatar(texto)}</p>
        )}

        <p className={"mt-0.5 text-right text-[11px] " + (autor === "humano" ? "opacity-60" : "text-[var(--color-fraco)]")}>
          <time>{hora}</time>
        </p>
      </div>
    </div>
  );
}
